import useMountEffect from '@restart/hooks/useMountEffect';
import { useContext } from 'react';
import { useDispatch, useStore } from 'react-redux';

import actions from '@/actions';
import { MessageContext } from '@/helpers/MessageProvider';
import { getMission } from '@/helpers/Requester';
import EventEmitter from '@/libs/EventEmitter';
import { isColdPosition } from '@/utils/MapUtils';

const useDJITelemetryHandler = (robot) => {
  const store = useStore();
  const dispatch = useDispatch();
  const { publishCommand } = useContext(MessageContext);

  useMountEffect(() => {
    const subscribeToken = EventEmitter.subscribe(`${robot.id}/telemetry/dji`, clarifyMessage);

    return () => {
      EventEmitter.unsubscribe(subscribeToken);
    };
  });

  const clarifyMessage = ({ header, payload }) => {
    const msgId = header.msgid;
    // console.debug(msgId, payload);

    switch (msgId) {
      case 101: // DRONE_OSD
        updateTelemetry(getKey(msgId), payload.data);
        updatePositionTelemetry(payload.data);
        break;

      case 121: // DRONE_FIRMWARE_VERSION
      case 122: // DRONE_DONGLE_INFOS
      case 131: // DRONE_H20_CAMERA
      case 132: // DRONE_CAMERA_WATERMARK
      case 133: // DRONE_CAMERAS
      case 134: // DRONE_CONTROL_PAYLOADS
      case 135: // DRONE_CLOUD_CONTROL_AUTH
      case 136: // DRONE_WPMZ_VERSION
      case 140: // DRONE_RTH_MODE
      case 141: // DRONE_OFFLINE_MAP_ENABLE
      case 142: // DRONE_REMAINING_POWER_FOR_RETURN_HOME
      case 143: // DRONE_COMMANDER_FLIGHT_MODE
      case 144: // DRONE_COMMANDER_FLIGHT_HEIGHT
      case 145: // DRONE_PAYLOADS
      case 146: // DRONE_GEO_CAGING_STATUS
      case 147: // DRONE_COMMANDER_MODE_LOST_ACTION
      case 149: // DRONE_MODE_CODE_REASON
      case 150: // DRONE_WIRELESS_LINK_TOPO
      case 201: // PILOT_OSD
      case 221: // PILOT_STATE_FIRMWARE_VERSION
      case 222: // PILOT_STATE_DONGLE_INFO
      case 223: // PILOT_STATE_LIVE_CAPACITY
      case 301: // DOCK_OSD_NETWORK
      case 302: // DOCK_OSD_STATUS
      case 303: // DOCK_OSD_MAINTENANCE
      case 321: // DOCK_FIRMWARE_VERSION
      case 322: // DOCK_DONGLE_INFOS
      case 323: // DOCK_LIVE_CAPACITY
      case 324: // DOCK_LIVE_STATUS
      case 325: // DOCK_RTCM_INFO
      case 326: // DOCK_DEPARTURE_TRAJECTORY
      case 327: // DOCK_MOBILITY_STATUS_NOTIFY
      case 328: // DOCK_AIR_TRANSFER_ENABLE
      case 350: // DOCK_WIRELESS_LINK_TOPO
        updateTelemetry(getKey(msgId), payload.data);
        break;

      case 382: // DOCK_EVENT
        // 응답이 요구되는 경우 자동 응답
        if (payload.need_reply === 1) {
          const { tid, bid, gateway, method } = payload;
          publishCommand(robot, 'remote_debug/events_reply', [tid, bid, Date.now(), gateway, method, { result: 0 }]);
        }

        // 미션 업로드 결과 수신 시
        if (payload.method === 'flighttask_ready') {
          publishTelemetry(payload);
        }

        // 미션 수행 상태 수신 시
        if (payload.method === 'flighttask_progress') {
          // TODO
        }

        break;

      case 299: // PILOT_SERVICES_REPLY
      case 399: // DOCK_SERVICES_REPLY
        publishTelemetry(payload);
        break;

      case 390: // TODO
        {
          const mission = store.getState().missions[robot.id];
          getMission(mission.id).then(({ success, data }) => {
            if (!success) return;

            const { tid, bid, method } = payload;
            const { s3ObjectUrl, s3Checksum } = data;
            publishCommand(robot, 'mission/requests_reply', [tid, bid, Date.now(), method, s3ObjectUrl, s3Checksum]);
          });
        }
        break;

      default:
        break;
    }
  };

  const getKey = (msgId) => {
    // prettier-ignore
    switch (msgId) {      
      // Drone
      case 101: return 'droneOsd';
      case 121: return 'droneFirmwareVersion';
      case 122: return 'droneDongleInfos';
      case 131: return 'h20Camera';
      case 132: return 'cameraWatermark';
      case 133: return 'cameras';
      case 134: return 'controlPayloads';
      case 135: return 'droneCloudControlAuth';
      case 136: return 'droneWpmzVersion';
      case 140: return 'droneRthMode';
      case 141: return 'droneOfflineMapEnable';
      case 142: return 'droneRemainingPowerForReturnHome';
      case 143: return 'droneCommanderFlightMode';
      case 144: return 'droneCommanderFlightHeight';
      case 145: return 'dronePayloads';
      case 146: return 'droneGeoCagingStatus';
      case 147: return 'droneCommanderModeLostAction';
      case 149: return 'droneModeCodeReason';
      case 150: return 'droneWirelessLinkTopo';
      case 181: return 'droneHms';

      // Pilot
      case 201: return 'pilotOsd';
      case 221: return 'pilotFirmwareVersion';
      case 222: return 'pilotDongleInfo';
      case 223: return 'pilotLiveCapacity';
      
      // Dock
      case 301: return 'dockOsdNetwork';
      case 302: return 'dockOsdStatus';
      case 303: return 'dockOsdMaintenance';
      case 321: return 'dockFirmwareVersion';
      case 322: return 'dockDongleInfos';
      case 323: return 'dockLiveCapacity';
      case 324: return 'dockLiveStatus';
      case 325: return 'dockRtcmInfo';
      case 326: return 'dockDepartureTrajectory';
      case 327: return 'dockMobilityStatusNotify';
      case 328: return 'dockAirTransferEnable';
      case 350: return 'dockWirelessLinkTopo';
      case 381: return 'dockHms';
      case 382: return 'dockEvent';

      // Services Reply
      case 299: return 'pilotServicesReply';
      case 399: return 'dockServicesReply';

      default: return;
    }
  };

  const updateTelemetry = (key, data) => {
    dispatch(actions.telemetry.set(robot.id, key, data));
  };

  const updatePositionTelemetry = (data) => {
    // GPS 데이터 유효하지 않은 경우
    if (isColdPosition(data.latitude, data.longitude)) return;

    EventEmitter.publish('position/update', {
      robotId: robot.id,
      position: { lat: data.latitude, lng: data.longitude },
    });
  };

  const publishTelemetry = (payload) => {
    EventEmitter.publish(`${robot.id}/telemetry/${payload.method}`, payload);
  };
};

export default useDJITelemetryHandler;
